import {
  getTypeOfPolicy,
  isFlagEnabled,
  sortInAscendingOrder,
} from "js/utilities";
import {
  stringFormatingToPascal,
  getBadgeText,
  getBadgeClassByPolicyStatus,
  getName,
  stringFormattingToCurrency,
} from "js/formatting";
import config from "config/config";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useTranslation } from "react-i18next";
import { useRowPagination } from "hooks/useRowPagination";
import { exportPolicies } from "service/policy/exportPolicies";
import { usePoliciesFiltering } from "hooks/usePoliciesFiltering";

import SortByDate from "components/General/SortByDate";
import SimpleDataRow from "components/General/SimpleDataRow";
import RowPagination from "components/General/RowPagination";
import FilterDialog from "components/AgentPortal/BookOfBusiness/FilterDialog";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import PolicyLookupModal from "components/AgentPortal/ModalDialogs/PolicyLookupModal";
import AgentSendReminderModal from "components/AgentPortal/ModalDialogs/AgentSendReminderModal";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";

export default function BookOfBusiness() {
  const { setValue } = useForm({
    defaultValues: {},
  });
  const { t } = useTranslation();
  const location = useLocation();
  const [policyType, setPolicyType] = useState();
  const { user } = useSelector((s) => s.user);
  const [orderBy, setOrderBy] = useState("oldest");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [isOpenPolicyLookup, setIsOpenPolicyLookup] = useState(false);
  const [type, setType] = useState("all");
  const [keyword, setKeyword] = useState();
  const [paymentStatus, setPaymentStatus] = useState();
  const [agentId, setAgentId] = useState();
  const [policyLookUpData, setPolicyLookUpData] = useState([]);
  const [filteredPolicyLookUpData, setFilteredPolicyLookUpData] = useState([]);
  const [allPolicy, setAllPolicy] = useState([]);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const { policies } = usePoliciesFiltering(
    keyword,
    type,
    paymentStatus,
    policyType
  );
  const [typeList, setTypeList] = useState();
  const [isOpenSendReminder, setIsOpenSendReminder] = useState(false);
  const [policyId, setPolicyId] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let copyPolicyLookUpData = [...filteredPolicyLookUpData];
    if (orderBy === "oldest") {
      copyPolicyLookUpData?.sort(function (a, b) {
        if (new Date(a.effectiveDate) < new Date(b.effectiveDate)) {
          return -1;
        }
        if (new Date(a.effectiveDate) > new Date(b.effectiveDate)) {
          return 1;
        }
        return 0;
      });
    } else if (orderBy === "newest") {
      copyPolicyLookUpData?.sort(function (a, b) {
        if (new Date(a.effectiveDate) > new Date(b.effectiveDate)) {
          return -1;
        }
        if (new Date(a.effectiveDate) < new Date(b.effectiveDate)) {
          return 1;
        }
        return 0;
      });
    }
    setFilteredPolicyLookUpData(copyPolicyLookUpData);
    setAllPolicy(copyPolicyLookUpData);
  }, [orderBy]);

  useEffect(() => {
    setValue(
      "policyStatus",
      searchParams.get("type") ? searchParams.get("type") : "all"
    );
  }, []);

  useEffect(() => {
    const policySelectionArrayForDocTab = policies.map((p) => ({
      ...p,
      value: p.contractID,
      displayAsValue: p.oldPolicy,
      displayAs: `${p.oldPolicy}: ${p?.insuredFullName}`,
    }));
    setPolicyFilterOptions([
      {
        value: "all",
        displayAs: t("AgentPolicySelector-alloption"),
        status: "",
        displayAsValue: t("AgentPolicySelector-alloption"),
      },
      ...policySelectionArrayForDocTab,
    ]);
    setPolicyLookUpData(policies);
  }, [policies]);

  useEffect(() => {
    setType(searchParams.get("type"));
    setKeyword(searchParams.get("keyword"));
    setTypeList(searchParams?.get("type")?.split(","));

    if (searchParams.get("paymentStatus"))
      setPaymentStatus(searchParams.get("paymentStatus"));
    else setPaymentStatus(undefined);
    if (typeList?.length > 1) {
      setValue("policyStatus", "");
    } else {
      setValue(undefined);
    }
    if (searchParams.get("agentId")) {
      setAgentId(searchParams.get("agentId"));
    } else {
      setAgentId(undefined);
    }
    if (searchParams.get("policyType")) {
      setPolicyType(searchParams.get("policyType"));
    } else {
      setPolicyType(undefined);
    }
  }, [searchParams, location.pathname]);

  useEffect(() => {
    let policies = [];
    if (policyLookUpData?.length > 0) {
      policyLookUpData?.forEach((policy) => {
        let party = policy?.parties?.find(
          (x) => x.person?.type?.toLowerCase() === "primary owner"
        );
        let filteredPolicy = {
          ownerFullName: policy?.ownerFullName,
          status: policy?.status,
          policyName: getName(party?.person),
          oldPolicy: policy?.oldPolicy,
          policyId: policy?.contractID,
          policyNumber: policy?.contractID,
          policyType: policy?.policyType,
          costOfInsurance: policy?.costOfInsurance,
          graceFlag: policy?.graceFlag,
          dueDate: policy?.premiumDueDate,
          premiumDueDate: policy?.premiumDueDate,
          effectiveDate: policy?.effectiveDate,
          dueAmount: policy?.modalPremiumAmountTotal
            ? stringFormattingToCurrency(policy?.modalPremiumAmountTotal)
            : "0.00",
          modalPremiumAmountTotal: policy?.modalPremiumAmountTotal,
          lapseDate:
            policy?.status?.toLowerCase() === "lapsed"
              ? policy?.statusDate
              : "",
          premiumAmount: policy?.modalPremiumAmountTotal,
          policyStatus:
            policy?.status?.toLowerCase() === "lapsed"
              ? "problem"
              : getBadgeClassByPolicyStatus(policy),
          policyStatusText: getBadgeText(policy),
          accountValue: policy?.cashValue?.accountValue,
          surrenderValue: policy?.cashValue?.surrenderCharge,
          productType: policy?.product?.type,
          firstName: party?.person?.firstName,
          lastName: party?.person?.lastName,
          product: {
            type: policy?.product?.type,
          },
        };
        policies.push(filteredPolicy);
      });
      if (agentId) {
        let policyIds = user?.agentHierarchy?.find(
          (x) => x.agentId === agentId
        )?.policyIds;
        if (policies?.length > 0) {
          let policyList = [];
          for (let i = 0; i < policies?.length; i++) {
            let contractID = policyIds?.find(
              (id) => id === policies[i]?.policyNumber
            );
            if (contractID) {
              policyList.push(policies[i]);
            }
            setFilteredPolicyLookUpData(
              sortInAscendingOrder(policyList, "lastName", "firstName")
            );
            setAllPolicy(
              sortInAscendingOrder(policyList, "lastName", "firstName")
            );
          }
        }
      } else {
        setFilteredPolicyLookUpData(
          sortInAscendingOrder(policies, "lastName", "firstName")
        );
        setAllPolicy(sortInAscendingOrder(policies, "lastName", "firstName"));
      }
    } else {
      setFilteredPolicyLookUpData([]);
      setAllPolicy([]);
    }
    //let typeList = type?.split(",");
    setTypeList(type?.split(","));
    if (searchParams.get("paymentStatus"))
      setPaymentStatus(searchParams.get("paymentStatus"));
    if (typeList?.length > 1) {
      setValue("policyStatus", "");
    } else {
      setValue(
        "policyStatus",
        searchParams.get("type") ? searchParams.get("type") : "all"
      );
    }
  }, [policyLookUpData, user]);

  const { rowsForDisplay: currentSelectedBusinesses, rowPaginationProps } =
    useRowPagination(filteredPolicyLookUpData);

  useBreadcrumb([
    { name: t("AgentBookOfBusiness-Home"), url: "/dashboard" },
    { name: t("AgentBookOfBusiness-title1"), url: "#" },
  ]);

  const getType = (type) => {
    const policyType = getTypeOfPolicy(type);

    if (config.ENABLE_ANNUITIES === "true" && policyType === "annuity") {
      return "annuityBookOfBusiness";
    } else return "bookOfBusiness";
  };

  async function exportPoliciesToCSV(policyType, policies) {
    let contractIds = [];
    policies?.map((policy) => {
      contractIds.push(policy?.policyId);
    });
    setIsLoading(true);
    if (contractIds) {
      await exportPolicies(policyType, contractIds)
        .then((response) => {
          if (response) {
            setIsLoading(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error("exportPolicies failing", error);
        });
    }
  }

  return (
    <>
      <div className="container mb-8">
        <h1>{t("AgentBookOfBusiness-title1")}</h1>
        <p>{t("AgentBookOfBusiness-sub-title")}</p>
        <hr />
      </div>
      <div className="container ui-component-spacing">
        <div className="flex flex-col items-centers justify-between  md:flex-row md:space-x-4">
          <div className="md:w-1/2">
            <AgentAutoCompleteComboBox
              labelName={t("AgentBookOfBusiness-dropdownLabel")}
              inputPlaceholderText={t("AgentFACStepOne-policyselect")}
              options={policyFilterOptions}
              selectedOption={selectedPolicyId}
              setSelectedOption={(option) => {
                if (option?.value !== "all") {
                  let selectedPolicy = allPolicy?.filter(
                    (p) => p.policyId === option?.value
                  );
                  setFilteredPolicyLookUpData(selectedPolicy);
                } else setFilteredPolicyLookUpData(allPolicy);
              }}
            />
          </div>
          <div className="flex flex-row items-center mt-4 space-x-4 justify-between md:justify-end lg:space-x-6 xl:mt-6">
            <div>
              <SortByDate
                changesDisplay="bookOfBusinessList"
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </div>
            <div className="flex">
              {isFlagEnabled("ENABLE_EXPORT_LAPSED_POLICIES") &&
                filteredPolicyLookUpData?.length > 0 &&
                stringFormatingToPascal(type) === "Lapsed" && (
                  <button
                    onClick={() =>
                      exportPoliciesToCSV(
                        stringFormatingToPascal(type),
                        filteredPolicyLookUpData
                      )
                    }
                    className="ui-btn-secondary flex-1"
                  >
                    Export
                    <span className="sr-only">
                      {stringFormatingToPascal(type)}
                    </span>
                  </button>
                )}

              <button
                className="ui-btn-primary flex items-center"
                onClick={() => setFilterDialogOpen(true)}
              >
                <svg
                  className="ui-icon w-3.5 h-3.5 mr-2"
                  focusable="false"
                  aria-hidden="true"
                  role="presentation"
                >
                  <use href="#ui-icon-filter"></use>
                </svg>
                Filter Options
              </button>
            </div>
          </div>
        </div>
        <EverlakeLoadingOverlay
          isLoading={isLoading}
          loadingText="Downloading..."
        >
          {filteredPolicyLookUpData?.length > 0 ? (
            <>
              {currentSelectedBusinesses.map((business, idx) => {
                return (
                  <SimpleDataRow
                    key={`agent-business-category-${business.category} - ${idx}`}
                    type={getType(business?.productType?.toUpperCase())}
                    data={business}
                    isOpenSendReminder={isOpenSendReminder}
                    setIsOpenSendReminder={setIsOpenSendReminder}
                    setPolicyId={setPolicyId}
                  />
                );
              })}
              {/* <!-- paging component --> */}
              <RowPagination {...rowPaginationProps} />
            </>
          ) : (
            <div className="relative mt-4">
              <p>{t("AgentBookOfBusiness-nocontract")}</p>
            </div>
          )}
        </EverlakeLoadingOverlay>
      </div>

      <FilterDialog
        dialogState={filterDialogOpen}
        setDialogState={setFilterDialogOpen}
      />
      {/* Policy Lookup Dialog */}
      <PolicyLookupModal
        isOpenPolicyLookup={isOpenPolicyLookup}
        setIsOpenPolicyLookup={setIsOpenPolicyLookup}
      />
      {/* Send customer a reminder Dialog */}
      <AgentSendReminderModal
        isOpenSendReminder={isOpenSendReminder}
        setIsOpenSendReminder={setIsOpenSendReminder}
        policyId={policyId}
      />
    </>
  );
}
